import type { UserPreferences } from '@court-sync/shared';
import type { PageTourKey } from './registry';
import { ORGANIZATION_SETTINGS_LEGACY_TOUR_KEYS } from './organizationSettingsTour';

type PreferencesLike = Pick<
  UserPreferences,
  | 'completedPageTours'
  | 'tourCompletedTermManagement'
  | 'tourCompletedCampManagement'
  | 'tourCompletedAcademyOwnerDashboard'
>;

const CLUB_COURT_HIRE_TAB_KEYS: PageTourKey[] = [
  'club-court-hire-bookings',
  'club-court-hire-rates',
  'club-court-hire-settings',
];

const CLUB_MEMBERS_TAB_KEYS: PageTourKey[] = [
  'club-members-roster',
  'club-members-types',
  'club-members-settings',
];

const CLUB_INVENTORY_TAB_KEYS: PageTourKey[] = [
  'club-inventory-items',
  'club-inventory-hires',
];

function isDeferredParentTourComplete(
  preferences: PreferencesLike | null | undefined,
  parentKey: PageTourKey,
  childKeys: PageTourKey[],
  createKey?: PageTourKey,
): boolean {
  if (preferences?.completedPageTours?.[parentKey]) {
    return true;
  }
  if (childKeys.some((key) => preferences?.completedPageTours?.[key] === true)) {
    return true;
  }
  if (createKey && preferences?.completedPageTours?.[createKey] === true) {
    return true;
  }
  return false;
}

function isClubCourtHirePageTourComplete(
  preferences: PreferencesLike | null | undefined,
): boolean {
  return isDeferredParentTourComplete(
    preferences,
    'club-court-hire',
    CLUB_COURT_HIRE_TAB_KEYS,
    'club-court-hire-rate-create',
  );
}

function isClubCourtHireRateCreateTourComplete(
  preferences: PreferencesLike | null | undefined,
): boolean {
  if (preferences?.completedPageTours?.['club-court-hire']) {
    return true;
  }
  return preferences?.completedPageTours?.['club-court-hire-rate-create'] === true;
}

function isClubMembersPageTourComplete(
  preferences: PreferencesLike | null | undefined,
): boolean {
  return isDeferredParentTourComplete(
    preferences,
    'club-members',
    CLUB_MEMBERS_TAB_KEYS,
    'club-members-type-create',
  );
}

function isClubMembersTypeCreateTourComplete(
  preferences: PreferencesLike | null | undefined,
): boolean {
  if (preferences?.completedPageTours?.['club-members']) {
    return true;
  }
  return preferences?.completedPageTours?.['club-members-type-create'] === true;
}

function isClubInventoryPageTourComplete(
  preferences: PreferencesLike | null | undefined,
): boolean {
  return isDeferredParentTourComplete(
    preferences,
    'club-inventory',
    CLUB_INVENTORY_TAB_KEYS,
    'club-inventory-item-create',
  );
}

function isClubInventoryItemCreateTourComplete(
  preferences: PreferencesLike | null | undefined,
): boolean {
  if (preferences?.completedPageTours?.['club-inventory']) {
    return true;
  }
  return preferences?.completedPageTours?.['club-inventory-item-create'] === true;
}

function isAcademyTermsTourComplete(preferences: PreferencesLike | null | undefined): boolean {
  if (preferences?.completedPageTours?.['academy-terms']) {
    return true;
  }
  if (preferences?.tourCompletedTermManagement === true) {
    return true;
  }
  return preferences?.completedPageTours?.['academy-terms-create'] === true;
}

function isOrganizationSettingsTourComplete(
  preferences: PreferencesLike | null | undefined,
): boolean {
  if (preferences?.completedPageTours?.['organization-settings']) {
    return true;
  }
  return ORGANIZATION_SETTINGS_LEGACY_TOUR_KEYS.every(
    (key) => preferences?.completedPageTours?.[key] === true,
  );
}

export function isPageTourComplete(
  preferences: PreferencesLike | null | undefined,
  tourKey: PageTourKey,
  legacyPreferenceKey?: keyof UserPreferences
): boolean {
  if (
    tourKey === 'organization-settings' ||
    ORGANIZATION_SETTINGS_LEGACY_TOUR_KEYS.includes(tourKey)
  ) {
    return isOrganizationSettingsTourComplete(preferences);
  }
  if ((tourKey as string) === 'academy-terms') {
    return isAcademyTermsTourComplete(preferences);
  }
  if (tourKey === 'academy-payments') {
    if (preferences?.completedPageTours?.['academy-payments-settings']) {
      return true;
    }
  }
  if (
    tourKey === 'academy-payments-billing-report' ||
    tourKey === 'academy-payments-settings' ||
    tourKey === 'academy-payments-starter'
  ) {
    if (preferences?.completedPageTours?.['academy-payments-starter']) {
      return true;
    }
    if (
      tourKey !== 'academy-payments-starter' &&
      (preferences?.completedPageTours?.['academy-payments-billing-report'] ||
        preferences?.completedPageTours?.['academy-payments-settings'])
    ) {
      return true;
    }
  }
  if (tourKey === 'club-court-hire-rate-create') {
    return isClubCourtHireRateCreateTourComplete(preferences);
  }
  if (
    tourKey === 'club-court-hire' ||
    CLUB_COURT_HIRE_TAB_KEYS.includes(tourKey)
  ) {
    return isClubCourtHirePageTourComplete(preferences);
  }
  if (tourKey === 'club-members-type-create') {
    return isClubMembersTypeCreateTourComplete(preferences);
  }
  if (tourKey === 'club-members' || CLUB_MEMBERS_TAB_KEYS.includes(tourKey)) {
    return isClubMembersPageTourComplete(preferences);
  }
  if (tourKey === 'club-inventory-item-create') {
    return isClubInventoryItemCreateTourComplete(preferences);
  }
  if (tourKey === 'club-inventory' || CLUB_INVENTORY_TAB_KEYS.includes(tourKey)) {
    return isClubInventoryPageTourComplete(preferences);
  }
  if (preferences?.completedPageTours?.[tourKey]) {
    return true;
  }
  if (legacyPreferenceKey && preferences?.[legacyPreferenceKey] === true) {
    return true;
  }
  return false;
}
